import React, { useState, useEffect } from 'react';
import { X, Upload, Download } from 'lucide-react';
import { candidatesAPI, jobsAPI, clientsAPI } from "../../services/api";
import { toast } from "react-toastify";

const ImportCandidatesModal = ({ onClose, onImport, jobs: initialJobs = [] }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [jobs, setJobs] = useState(initialJobs);
  const [clients, setClients] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [fetchingData, setFetchingData] = useState(false);

  // Fetch jobs and clients on component mount
  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      try {
        const [jobsResponse, clientsResponse] = await Promise.all([
          jobsAPI.getAllJobs(),
          clientsAPI.getAllClients()
        ]);
        
        let jobsData = Array.isArray(jobsResponse.data) ? jobsResponse.data : jobsResponse.data.content || [];
        let clientsData = Array.isArray(clientsResponse.data) ? clientsResponse.data : clientsResponse.data.content || [];
        
        setJobs(jobsData);
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        toast.error('Failed to load jobs and clients');
      } finally {
        setFetchingData(false);
      }
    };
    
    fetchData();
  }, []);

  // Auto-populate clientId when job is selected
  const handleJobChange = (jobId) => {
    setSelectedJobId(jobId);
    
    if (jobId) {
      const selectedJob = jobs.find(job => job.id === jobId);
      if (selectedJob && selectedJob.clientId) {
        setSelectedClientId(selectedJob.clientId);
      } else if (selectedJob && selectedJob.client && selectedJob.client.id) {
        setSelectedClientId(selectedJob.client.id);
      } else {
        // Reset client selection if job doesn't have associated client
        setSelectedClientId('');
      }
    } else {
      setSelectedClientId('');
    }
  };

  const downloadErrorCSV = () => {
    const errors = importResult?.failedImports || [];
    const header = 'Row,Email / Name,Reason';
    const rows = errors.map(e => {
      const identifier = e.data?.primaryEmail ||
        [e.data?.firstName, e.data?.lastName].filter(Boolean).join(' ') || '';
      return [String(e.rowNumber ?? ''), identifier, e.reason || '']
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });
    const csv = [header, ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'candidate_import_errors.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!selectedJobId) {
      toast.error('Please select a job');
      return;
    }

    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('jobId', selectedJobId);
      formData.append('clientId', selectedClientId);

      const response = await candidatesAPI.importCandidates(formData);
      
      console.log('=== IMPORT RESPONSE ===');
      console.log('Full Response:', response);
      console.log('Response Data:', response.data);
      console.log('Response Status:', response.status);
      console.log('Response Headers:', response.headers);
      
      // Validate response
      if (!response || !response.data) {
        console.error('Invalid response - missing data');
        throw new Error('Invalid response from server');
      }

      const responseData = response.data;
      console.log('Response Data Object:', responseData);
      console.log('Keys in responseData:', Object.keys(responseData));

      // Get counts from the summary object
      const successCount = responseData.summary?.successCount || 0;
      const failedCount = responseData.summary?.failedCount || 0;
      const existingCount = responseData.summary?.existingCount || 0;
      const totalProcessed = responseData.summary?.totalProcessed || 0;

      console.log('Import Summary:', { successCount, failedCount, existingCount, totalProcessed });

      // Store result for display
      setImportResult(responseData);

      // Check if import was successful based on API response
      if (response.status !== 200) {
        console.warn('Import failed - non-200 status:', response.status);
        toast.error(responseData.message || 'Failed to import candidates');
        return;
      }

      // Check if any records were imported
      if (successCount === 0) {
        console.warn('No records imported successfully');
        let message = 'No candidates were imported.';

        if (failedCount > 0) {
          const firstFailure = responseData.failedImports?.[0];
          const reason = firstFailure?.reason || 'Check file format';
          message = `Import failed: ${reason} (and ${failedCount} total failures)`;
        } else if (existingCount > 0) {
          message = `${existingCount} candidate(s) already exist for this job/client.`;
        }

        toast.warning(message);
        return;
      }

      // Build success message with details
      let successMessage = `Successfully imported ${successCount} candidate(s)`;
      if (existingCount > 0) {
        successMessage += ` (${existingCount} already existed)`;
      }
      if (failedCount > 0) {
        successMessage += ` - ${failedCount} failed`;
      }

      console.log('Import successful! Details:', { successCount, failedCount, existingCount });
      toast.success(successMessage);

      // Reset form only when fully successful (no failures to display)
      if (failedCount === 0) {
        setFile(null);
        setSelectedJobId('');
        setSelectedClientId('');
        onImport();
      }
    } catch (error) {
      console.error('=== IMPORT ERROR ===');
      console.error('Error Object:', error);
      console.error('Error Message:', error.message);
      console.error('Error Response:', error.response);
      console.error('Error Response Data:', error.response?.data);
      console.error('Error Response Status:', error.response?.status);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to import candidates';
      console.error('Final Error Message:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Import Candidates</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Job *
            </label>
            <select
              value={selectedJobId}
              onChange={(e) => handleJobChange(e.target.value)}
              disabled={fetchingData}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Choose a Job --</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.jobTitle || job.title || job.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Client * {selectedClientId && <span className="text-xs text-green-600">(Auto-populated)</span>}
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              disabled={fetchingData}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Choose a Client --</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name || client.clientName || client.companyName}
                </option>
              ))}
            </select>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="mb-4">
              <label className="cursor-pointer">
                <span className="text-blue-600 hover:text-blue-700 font-medium">
                  Choose a file
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
              </label>
              <p className="text-sm text-gray-500 mt-1">or drag and drop</p>
            </div>
            {file && (
              <p className="text-sm text-gray-700 font-medium">{file.name}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">Excel or CSV files only</p>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Imported', value: importResult.summary?.successCount || 0, cls: 'bg-green-50 text-green-700' },
                  { label: 'Failed',   value: importResult.summary?.failedCount   || 0, cls: 'bg-red-50 text-red-700'   },
                  { label: 'Existing', value: importResult.summary?.existingCount || 0, cls: 'bg-yellow-50 text-yellow-700' },
                ].map(({ label, value, cls }) => (
                  <div key={label} className={`rounded-lg p-2 text-center ${cls}`}>
                    <p className="text-xl font-bold">{value}</p>
                    <p className="text-xs font-medium mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {importResult.failedImports?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-red-600">
                      Failed rows ({importResult.failedImports.length})
                    </p>
                    <button
                      onClick={downloadErrorCSV}
                      className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded px-2.5 py-1.5 hover:bg-gray-50 transition"
                    >
                      <Download size={13} />
                      Download error report
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-semibold text-red-700 w-10">Row</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-red-700">Email / Name</th>
                          <th className="px-2 py-1.5 text-left font-semibold text-red-700">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {importResult.failedImports.map((err, i) => (
                          <tr key={i} className="bg-white hover:bg-red-50">
                            <td className="px-2 py-1.5 font-semibold text-red-700">{err.rowNumber}</td>
                            <td className="px-2 py-1.5 text-gray-700">
                              {err.data?.primaryEmail ||
                                [err.data?.firstName, err.data?.lastName].filter(Boolean).join(' ') ||
                                '—'}
                            </td>
                            <td className="px-2 py-1.5 text-red-600">{err.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file || !selectedJobId || !selectedClientId || loading || fetchingData}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportCandidatesModal;
